import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography} from "@mui/material";
import {useLocation} from "react-router-dom";
import {useRuntimeConfig} from "../config/RuntimeConfigProvider";
import {
  ANALYTICS_CONSENT_KEY, createPageViewTracker, readAnalyticsConsent,
  saveAnalyticsConsent, validMeasurementId,
} from "../helper/analytics";

const tracker = createPageViewTracker();
const AnalyticsPreferencesContext = createContext(null);
const explanation = "We send limited page measurements to Google Analytics without analytics cookies. Accept optional analytics cookies for more complete visit measurement. If you reject cookies, limited measurement continues. You can change your cookie choice from the account or Settings menu.";

export const useAnalyticsPreferences = () => useContext(AnalyticsPreferencesContext);

export function useAnalyticsPreferencesMenu(closeMenu) {
  const openPreferences = useAnalyticsPreferences();
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    if (requested) {
      setRequested(false);
      openPreferences?.();
    }
  }, [requested, openPreferences]);
  // Closing first lets the menu restore focus before the dialog captures it.
  return openPreferences ? () => {
    closeMenu();
    setRequested(true);
  } : null;
}

export default function AnalyticsConsent({children}) {
  const {PUBLIC_GA4_MEASUREMENT_ID} = useRuntimeConfig();
  const measurementId = validMeasurementId(PUBLIC_GA4_MEASUREMENT_ID);
  const {pathname} = useLocation();
  const [consent, setConsent] = useState(readAnalyticsConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);

  useEffect(() => () => tracker.disable(), []);

  useEffect(() => {
    const syncConsent = (event) => {
      if (event.key === ANALYTICS_CONSENT_KEY || event.key === null) {
        const value = readAnalyticsConsent();
        tracker.setConsent(value);
        setConsent(value);
      }
    };
    window.addEventListener("storage", syncConsent);
    return () => window.removeEventListener("storage", syncConsent);
  }, []);

  useEffect(() => {
    if (measurementId) {
      tracker.track(measurementId, pathname, consent);
    } else {
      tracker.disable();
    }
  }, [measurementId, consent, pathname]);

  function choose(value) {
    // Apply the cookie choice before another navigation or a pending tag load.
    tracker.setConsent(value);
    saveAnalyticsConsent(value);
    setConsent(value);
    setPreferencesOpen(false);
  }

  const actions = (
    <Stack direction="row" spacing={1} sx={{flexWrap: "wrap", gap: 1}}>
      <Button variant="outlined" onClick={() => choose("rejected")}>Reject cookies</Button>
      <Button variant="outlined" onClick={() => choose("accepted")}>Accept cookies</Button>
    </Stack>
  );

  return (
    <AnalyticsPreferencesContext.Provider value={measurementId ? openPreferences : null}>
      {children}
      {measurementId ? <>
      {consent === null ? (
        <Paper component="section" aria-label="Analytics cookies" elevation={8}
          sx={{position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1400,
            maxHeight: "70vh", overflowY: "auto", p: {xs: 2, sm: 3}}}>
          <Stack spacing={2} sx={{maxWidth: 960, mx: "auto"}}>
            <Typography component="h2" variant="h6">Analytics cookies</Typography>
            <Typography>{explanation}</Typography>
            {actions}
          </Stack>
        </Paper>
      ) : null}
      <Dialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)}
        aria-labelledby="analytics-preferences-title" fullWidth maxWidth="sm" sx={{zIndex: 2700}}>
        <DialogTitle id="analytics-preferences-title">Analytics preferences</DialogTitle>
        <DialogContent>
          <Typography>{explanation}</Typography>
          <Typography sx={{mt: 2}}>Optional analytics cookies are currently {consent || "not selected"}.</Typography>
        </DialogContent>
        <DialogActions sx={{p: 2, flexWrap: "wrap", gap: 1}}>
          <Button autoFocus onClick={() => setPreferencesOpen(false)}>Close</Button>
          {actions}
        </DialogActions>
      </Dialog>
      </> : null}
    </AnalyticsPreferencesContext.Provider>
  );
}
